import { useState, useEffect, useRef } from 'react';
import { hexToRgba, resolveLinkProps, resolveImage } from '../../utils';
import { getButtonIcon, resolveIconKey } from '../../buttonIcons';

// ── Static class lookups ────────────────────────────

const PADDING = {
    none: '', xs: 'px-2 py-1', sm: 'px-3 py-2', md: 'px-4 py-3', lg: 'px-6 py-5', xl: 'px-8 py-7',
};
const MARGIN_BOTTOM = {
    none: '', xs: 'mb-1', sm: 'mb-2', md: 'mb-4', lg: 'mb-6', xl: 'mb-8',
};
const RADIUS = {
    none: '', sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', full: 'rounded-full',
};
const SHADOW = {
    none: '', sm: 'shadow-sm', md: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl', '2xl': 'shadow-2xl',
};
const TEXT_ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const FONT_SIZE = {
    xs: 'text-xs', sm: 'text-sm', base: 'text-base', lg: 'text-lg', xl: 'text-xl',
    '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl', '5xl': 'text-5xl', '6xl': 'text-6xl',
};
const FONT_WEIGHT = {
    light: 'font-light', normal: 'font-normal', medium: 'font-medium',
    semibold: 'font-semibold', bold: 'font-bold', extrabold: 'font-extrabold',
};
const HEADING_TAG = { h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6' };

const SPACER_HEIGHT = { sm: '16px', md: '32px', lg: '64px', xl: '96px' };
const WIDTH_CLS = { full: 'w-full', '3/4': 'w-3/4', '1/2': 'w-1/2', '1/3': 'w-1/3', '1/4': 'w-1/4', auto: 'w-auto' };
const ASPECT_CLS = { auto: '', '1:1': 'aspect-square', '16:9': 'aspect-video', '4:3': 'aspect-[4/3]', '3:2': 'aspect-[3/2]', '2:3': 'aspect-[2/3]', '9:16': 'aspect-[9/16]' };

const HOVER_CLASSES = {
    none: '',
    float: 'transition-transform duration-300 hover:-translate-y-1',
    scale: 'transition-transform duration-300 hover:scale-105',
    'glow-pulse': 'transition-shadow duration-300',
    tilt: 'transition-transform duration-300 hover:[transform:perspective(600px)_rotateY(-3deg)_rotateX(3deg)]',
    'blur-reveal': 'transition-all duration-300 hover:blur-0',
};

const LETTER_SPACING = {
    tighter: '-0.05em', tight: '-0.025em', normal: '0em', wide: '0.025em', wider: '0.05em', widest: '0.1em',
};
const LINE_HEIGHT = {
    none: '1', tight: '1.15', snug: '1.3', normal: '1.5', relaxed: '1.65', loose: '2',
};
const MAX_WIDTH = {
    full: 'none', '6xl': '72rem', '5xl': '64rem', '4xl': '56rem', '3xl': '48rem', '2xl': '42rem',
    xl: '36rem', lg: '32rem', md: '28rem', sm: '24rem',
};

// ── Entrance animation CSS (injected once) ──────────
const ENTRANCE_ANIMATIONS = {
    'fade-up': { from: 'opacity:0;transform:translateY(30px)', to: 'opacity:1;transform:translateY(0)' },
    'fade-in': { from: 'opacity:0', to: 'opacity:1' },
    'fade-down': { from: 'opacity:0;transform:translateY(-30px)', to: 'opacity:1;transform:translateY(0)' },
    'slide-left': { from: 'opacity:0;transform:translateX(-40px)', to: 'opacity:1;transform:translateX(0)' },
    'slide-right': { from: 'opacity:0;transform:translateX(40px)', to: 'opacity:1;transform:translateX(0)' },
    'zoom-in': { from: 'opacity:0;transform:scale(0.85)', to: 'opacity:1;transform:scale(1)' },
    'blur-in': { from: 'opacity:0;filter:blur(8px)', to: 'opacity:1;filter:blur(0)' },
    'flip-up': { from: 'opacity:0;transform:perspective(600px) rotateX(15deg)', to: 'opacity:1;transform:perspective(600px) rotateX(0)' },
};

const ANIM_CSS = Object.entries(ENTRANCE_ANIMATIONS).map(([name, {from, to}]) =>
    `@keyframes we-${name}{from{${from}}to{${to}}}`
).join('\n');

// ── Icon SVGs for icon_text / list widgets ──────────

const WIDGET_ICONS = {
    star: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
    check: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>,
    heart: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>,
    shield: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
    zap: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>,
    target: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2}/><circle cx="12" cy="12" r="6" strokeWidth={2}/><circle cx="12" cy="12" r="2" strokeWidth={2}/></svg>,
    award: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>,
    globe: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>,
    users: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>,
    code: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>,
    phone: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>,
    mail: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
    x: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>,
};

// ── List bullet icons ───────────────────────────────

const LIST_ICONS = {
    check: (color) => <svg className="w-4 h-4 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>,
    arrow: (color) => <svg className="w-4 h-4 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>,
    star: (color) => <svg className="w-4 h-4 shrink-0" style={{ color }} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
    circle: (color) => <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: color }} />,
    x: (color) => <svg className="w-4 h-4 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>,
};

// ── Social media SVG icons ──────────────────────────

const SOCIAL_ICONS = {
    facebook: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    instagram: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
    twitter: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    linkedin: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    youtube: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    tiktok: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
    github: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>,
    whatsapp: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
};

// ── Video embed helpers ─────────────────────────────

function parseVideoUrl(url) {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return { provider: 'youtube', id: ytMatch[1] };
    const vmMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vmMatch) return { provider: 'vimeo', id: vmMatch[1] };
    return null;
}

// ── Animated stat counter ───────────────────────────

function AnimatedCounter({ value, enableAnimations }) {
    const ref = useRef(null);
    const [display, setDisplay] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const numericValue = typeof value === 'string' ? parseInt(value.replace(/[^0-9]/g, ''), 10) : value;
    const isNum = !isNaN(numericValue) && numericValue > 0;

    useEffect(() => {
        if (!enableAnimations || !isNum || hasAnimated) return;
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                obs.disconnect();
                setHasAnimated(true);
                const duration = 1500;
                const start = performance.now();
                const step = (now) => {
                    const progress = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setDisplay(Math.round(eased * numericValue));
                    if (progress < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
            }
        }, { threshold: 0.3 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [enableAnimations, isNum, numericValue, hasAnimated]);

    if (!enableAnimations || !isNum) return <>{value}</>;
    return <span ref={ref}>{display}</span>;
}

// ── Wrapper: applies common style properties ────────
// Reads from widget.style (builder) with fallback to widget.data (legacy)

function WidgetWrapper({ widget, children, visible, widgetIndex }) {
    const st = { ...(widget.data || {}), ...(widget.style || {}) };
    const cls = [
        PADDING[st.padding] || '',
        MARGIN_BOTTOM[st.margin_bottom] || '',
        RADIUS[st.border_radius] || '',
        SHADOW[st.shadow] || '',
        TEXT_ALIGN[st.text_align] || '',
        FONT_SIZE[st.font_size] || '',
        FONT_WEIGHT[st.font_weight] || '',
        st.hide_mobile ? 'hidden md:block' : '',
        st.hide_desktop ? 'md:hidden' : '',
        st.glass ? 'backdrop-blur-md bg-white/10 border border-white/20' : '',
        HOVER_CLASSES[st.hover_effect] || '',
        st.blur_reveal_init && st.hover_effect === 'blur-reveal' ? 'blur-sm' : '',
    ].filter(Boolean).join(' ');

    const style = {};
    if (st.text_color) style.color = st.text_color;
    if (st.bg_color && widget.type === 'card') style.backgroundColor = st.bg_color;
    else if (st.bg_color && !st.glass) style.backgroundColor = st.bg_color;
    if (st.opacity != null && st.opacity !== 100) style.opacity = st.opacity / 100;

    // Typography
    if (st.letter_spacing && LETTER_SPACING[st.letter_spacing]) style.letterSpacing = LETTER_SPACING[st.letter_spacing];
    if (st.line_height && LINE_HEIGHT[st.line_height]) style.lineHeight = LINE_HEIGHT[st.line_height];
    if (st.max_width && MAX_WIDTH[st.max_width] && st.max_width !== 'full') style.maxWidth = MAX_WIDTH[st.max_width];

    // Border
    if (st.border_width && st.border_width !== '0') {
        style.borderWidth = `${st.border_width}px`;
        style.borderStyle = 'solid';
        style.borderColor = st.border_color || '#e5e7eb';
    }

    // Glow
    if (st.glow) {
        const glowColor = st.glow_color || '#8b5cf6';
        style.boxShadow = `0 0 20px ${hexToRgba(glowColor, 0.4)}, 0 0 40px ${hexToRgba(glowColor, 0.2)}`;
    }

    // Gradient text
    if (st.gradient_text) {
        style.backgroundImage = `linear-gradient(135deg, ${st.gradient_from || '#8b5cf6'}, ${st.gradient_to || '#ec4899'})`;
        style.WebkitBackgroundClip = 'text';
        style.WebkitTextFillColor = 'transparent';
        style.backgroundClip = 'text';
    }

    // Per-widget entrance animation
    const anim = st.animation;
    if (anim && ENTRANCE_ANIMATIONS[anim]) {
        const delay = st.animation_delay ? `${st.animation_delay}ms` : (widgetIndex ? `${widgetIndex * 100}ms` : '0ms');
        style.animation = `we-${anim} 0.6s ${delay} both cubic-bezier(0.16,1,0.3,1)`;
    }

    return <div className={cls || undefined} style={Object.keys(style).length ? style : undefined}>{children}</div>;
}

// ── Individual widget renderers ─────────────────────

function HeadingWidget({ data, s, style }) {
    const Tag = HEADING_TAG[data.level] || HEADING_TAG[data.tag] || 'h2';
    const headingStyle = {};
    const st = style || {};
    if (st.gradient_text) {
        headingStyle.backgroundImage = `linear-gradient(135deg, ${st.gradient_from || '#8b5cf6'}, ${st.gradient_to || '#ec4899'})`;
        headingStyle.WebkitBackgroundClip = 'text';
        headingStyle.WebkitTextFillColor = 'transparent';
        headingStyle.backgroundClip = 'text';
    } else if (data.gradient_text && data.gradient_from && data.gradient_to) {
        headingStyle.backgroundImage = `linear-gradient(135deg, ${data.gradient_from}, ${data.gradient_to})`;
        headingStyle.WebkitBackgroundClip = 'text';
        headingStyle.WebkitTextFillColor = 'transparent';
        headingStyle.backgroundClip = 'text';
    } else {
        headingStyle.color = st.text_color || data.text_color || s.text;
    }
    return <Tag style={headingStyle}>{data.text || ''}</Tag>;
}

function RichTextWidget({ data, s }) {
    return (
        <div
            className="prose prose-sm max-w-none"
            style={{ color: data.text_color || s.textSub }}
            dangerouslySetInnerHTML={{ __html: data.html || data.text || '' }}
        />
    );
}

function ImageWidget({ data, mediaFolders }) {
    let src = null;
    if (data.image_folder_id && data.image_file_id && mediaFolders[data.image_folder_id]) {
        const found = mediaFolders[data.image_folder_id].find(f => String(f.id) === String(data.image_file_id));
        if (found) src = found.image_url;
    }
    if (!src) src = data.image_url;

    const radiusCls = RADIUS[data.border_radius] || 'rounded-lg';
    const widthCls = WIDTH_CLS[data.width] || 'w-full';
    const aspectCls = ASPECT_CLS[data.aspect] || '';
    const maxH = data.max_height && data.max_height !== 'none' ? {maxHeight: `${data.max_height}px`} : {};
    const fitCls = data.fit === 'contain' ? 'object-contain' : 'object-cover';

    if (!src) {
        return (
            <div className={`${widthCls} ${aspectCls || 'aspect-video'} bg-gray-100 border-2 border-dashed border-gray-300 ${radiusCls} flex flex-col items-center justify-center`} style={maxH}>
                <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"/></svg>
                <span className="text-xs text-gray-400">Agrega una imagen</span>
            </div>
        );
    }

    const hoverCls = data.hover_zoom ? 'hover:scale-105 transition-transform duration-500' : '';

    return (
        <div className={`${widthCls} overflow-hidden ${radiusCls} ${aspectCls}`} style={maxH}>
            <img src={src} alt={data.alt || ''} className={`w-full h-full ${fitCls} ${hoverCls}`} loading="lazy"/>
        </div>
    );
}

function ButtonWidget({ data, s, primaryColor }) {
    const btnStyle = data.style || 'solid';
    const icon = data.icon ? getButtonIcon(resolveIconKey(data.icon)) : null;
    const bgColor = data.bg_color || primaryColor;
    const txtColor = data.text_color || '#ffffff';
    const fullWidth = data.full_width ? 'w-full' : 'inline-flex';
    const sizeCls = data.size === 'sm' ? 'px-4 py-2 text-sm' : data.size === 'lg' ? 'px-8 py-4 text-lg' : 'px-6 py-3';
    const baseClasses = `${fullWidth} items-center justify-center gap-2 font-semibold transition-all duration-300 ${sizeCls}`;

    let cls = baseClasses;
    const inlineStyle = {};

    switch (btnStyle) {
        case 'outline':
            cls += ' border-2 rounded-lg bg-transparent hover:opacity-80';
            inlineStyle.borderColor = bgColor;
            inlineStyle.color = bgColor;
            break;
        case 'pill':
            cls += ' rounded-full hover:opacity-90';
            inlineStyle.backgroundColor = bgColor;
            inlineStyle.color = txtColor;
            break;
        case 'ghost':
            cls += ' rounded-lg hover:opacity-70';
            inlineStyle.color = bgColor;
            break;
        default:
            cls += ' rounded-lg hover:opacity-90';
            inlineStyle.backgroundColor = bgColor;
            inlineStyle.color = txtColor;
            break;
    }

    if (data.glow && bgColor) {
        inlineStyle.boxShadow = `0 0 20px ${hexToRgba(bgColor, 0.4)}`;
    }

    return (
        <a {...resolveLinkProps(data.link)} className={cls} style={inlineStyle}>
            {icon && <span className="w-4 h-4">{icon}</span>}
            {data.text || 'Click'}
        </a>
    );
}

function IconTextWidget({ data, s, primaryColor }) {
    const iconKey = data.icon || 'star';
    const iconEl = WIDGET_ICONS[iconKey] || WIDGET_ICONS.star;
    const position = data.icon_position || 'left';
    const isTop = position === 'top';

    return (
        <div className={`flex ${isTop ? 'flex-col items-center text-center' : 'items-start'} gap-4`}>
            <span
                className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: hexToRgba(data.icon_color || primaryColor, 0.12), color: data.icon_color || primaryColor }}
            >
                <span className="w-6 h-6">{iconEl}</span>
            </span>
            <div className="flex-1 min-w-0">
                {data.title && <h4 className="font-semibold text-base" style={{ color: data.text_color || s.text }}>{data.title}</h4>}
                {data.description && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: s.textSub }}>{data.description}</p>}
            </div>
        </div>
    );
}

function ListWidget({ data, s, primaryColor }) {
    const items = data.items || [];
    const listStyle = data.list_style || data.style || 'bullet';
    const iconColor = data.icon_color || primaryColor;

    return (
        <ul className="space-y-2">
            {items.map((item, i) => {
                const text = typeof item === 'string' ? item : (item.text || '');
                const itemIcon = typeof item === 'object' ? (item.icon || null) : null;

                let bullet = null;
                if (listStyle === 'numbered') {
                    bullet = <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: hexToRgba(iconColor, 0.1), color: iconColor }}>{i + 1}</span>;
                } else if (listStyle === 'check' || itemIcon === 'check') {
                    bullet = LIST_ICONS.check(iconColor);
                } else if (listStyle === 'arrow') {
                    bullet = LIST_ICONS.arrow(iconColor);
                } else if (listStyle === 'x' || itemIcon === 'x') {
                    bullet = LIST_ICONS.x(iconColor);
                } else {
                    const iconKey = itemIcon || 'circle';
                    const renderer = LIST_ICONS[iconKey] || LIST_ICONS.circle;
                    bullet = renderer(iconColor);
                }

                return (
                    <li key={i} className="flex items-start gap-2.5">
                        {bullet}
                        <span className="text-sm leading-relaxed" style={{ color: data.text_color || s.text }}>{text}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function VideoEmbedWidget({ data }) {
    const parsed = parseVideoUrl(data.url);
    const widthCls = WIDTH_CLS[data.width] || 'w-full';
    const maxH = data.max_height && data.max_height !== 'none' ? {maxHeight: `${data.max_height}px`} : {};

    if (!parsed) {
        return (
            <div className={`${widthCls} aspect-video bg-gray-900 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center`} style={maxH}>
                <svg className="w-12 h-12 text-gray-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"/></svg>
                <span className="text-xs text-gray-500">Agrega una URL de YouTube o Vimeo</span>
            </div>
        );
    }

    const src = parsed.provider === 'youtube'
        ? `https://www.youtube-nocookie.com/embed/${parsed.id}?rel=0`
        : `https://player.vimeo.com/video/${parsed.id}`;

    return (
        <div className={`${widthCls} relative aspect-video rounded-lg overflow-hidden`} style={maxH}>
            <iframe src={src} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" title={data.title || 'Video'}/>
        </div>
    );
}

function SpacerWidget({ data }) {
    const height = SPACER_HEIGHT[data.height] || SPACER_HEIGHT.md;
    return <div style={{ height }} />;
}

function DividerWidget({ data, s, primaryColor }) {
    const variant = data.style || 'solid';
    const width = data.width || 'full';
    const widthCls = width === 'sm' ? 'max-w-[120px]' : width === 'md' ? 'max-w-xs' : width === 'lg' ? 'max-w-lg' : 'w-full';
    const color = data.color || s.textSub || '#e5e7eb';

    if (variant === 'gradient') {
        return <div className={`${widthCls} mx-auto h-px`} style={{ background: `linear-gradient(to right, transparent, ${primaryColor}, transparent)` }} />;
    }
    if (variant === 'glow') {
        return <div className={`${widthCls} mx-auto h-px`} style={{ backgroundColor: primaryColor, boxShadow: `0 0 8px ${hexToRgba(primaryColor, 0.5)}` }} />;
    }

    const borderStyle = variant === 'dashed' ? 'dashed' : variant === 'dotted' ? 'dotted' : 'solid';
    return <hr className={`${widthCls} mx-auto border-0 border-t`} style={{ borderTopWidth: '1px', borderTopStyle: borderStyle, borderTopColor: color }} />;
}

function SocialLinksWidget({ data, s, primaryColor }) {
    const links = data.links || {};
    const items = data.items || [];
    const size = data.size || 'md';
    const sizeCls = size === 'sm' ? 'w-8 h-8' : size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';
    const iconColor = data.icon_color || primaryColor;

    // Support both legacy {links: {platform: url}} and new {items: [{platform, url}]}
    let entries = Object.entries(links).filter(([, url]) => url);
    if (entries.length === 0 && items.length > 0) {
        entries = items.filter(i => i.url).map(i => [i.platform, i.url]);
    }
    if (entries.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-3">
            {entries.map(([platform, url]) => {
                const icon = SOCIAL_ICONS[platform];
                if (!icon) return null;
                return (
                    <a key={platform} {...resolveLinkProps(url)}
                        className={`${sizeCls} rounded-full flex items-center justify-center transition-all duration-300 hover:opacity-80 hover:-translate-y-0.5`}
                        style={{ backgroundColor: hexToRgba(iconColor, 0.1), color: iconColor }}>
                        {icon}
                    </a>
                );
            })}
        </div>
    );
}

function StatCounterWidget({ data, s, primaryColor, enableAnimations }) {
    const prefix = data.prefix || '';
    const suffix = data.suffix || '';

    return (
        <div className="text-center">
            <div className="font-bold" style={{ color: data.value_color || primaryColor, fontSize: '2.5rem', lineHeight: 1.1 }}>
                {prefix}<AnimatedCounter value={data.value || 0} enableAnimations={enableAnimations} />{suffix}
            </div>
            {data.label && <p className="text-sm mt-2 font-medium" style={{ color: data.text_color || s.textSub }}>{data.label}</p>}
        </div>
    );
}

function TestimonialCardWidget({ data, s, primaryColor, mediaFolders }) {
    const rating = data.rating || 0;
    let avatarSrc = null;
    if (data.avatar_folder_id && data.avatar_file_id && mediaFolders[data.avatar_folder_id]) {
        const found = mediaFolders[data.avatar_folder_id].find(f => String(f.id) === String(data.avatar_file_id));
        if (found) avatarSrc = found.image_url;
    }
    if (!avatarSrc) avatarSrc = data.avatar_url;

    return (
        <div className="rounded-xl p-5 border" style={{
            backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
            borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
        }}>
            {rating > 0 && (
                <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map(i => (
                        <svg key={i} className="w-4 h-4" fill={i <= rating ? (primaryColor || '#facc15') : 'none'}
                            stroke={i <= rating ? 'none' : (s.dark ? 'rgba(255,255,255,0.2)' : '#d1d5db')} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                        </svg>
                    ))}
                </div>
            )}
            {data.quote && <p className="text-sm leading-relaxed italic mb-4" style={{ color: s.text }}>"{data.quote}"</p>}
            <div className="flex items-center gap-3">
                {avatarSrc ? (
                    <img src={avatarSrc} alt={data.author || ''} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{backgroundColor: hexToRgba(primaryColor || '#6366f1', 0.15), color: primaryColor || '#6366f1'}}>
                        {(data.author || '?')[0]?.toUpperCase()}
                    </div>
                )}
                <div>
                    {data.author && <p className="text-sm font-semibold" style={{ color: s.text }}>{data.author}</p>}
                    {data.role && <p className="text-xs" style={{ color: s.textSub }}>{data.role}</p>}
                </div>
            </div>
        </div>
    );
}

function BadgeWidget({ data, s, primaryColor }) {
    const variant = data.variant || 'solid';
    const icon = data.icon ? (WIDGET_ICONS[data.icon] || null) : null;
    const color = data.color || primaryColor;

    const baseClasses = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold';
    const style = {};

    switch (variant) {
        case 'outline':
            style.border = `1.5px solid ${color}`;
            style.color = color;
            style.backgroundColor = 'transparent';
            break;
        case 'glass':
            style.backgroundColor = hexToRgba(color, 0.15);
            style.color = color;
            style.backdropFilter = 'blur(8px)';
            style.border = `1px solid ${hexToRgba(color, 0.2)}`;
            break;
        case 'glow':
            style.backgroundColor = color;
            style.color = '#ffffff';
            style.boxShadow = `0 0 12px ${hexToRgba(color, 0.5)}`;
            break;
        default:
            style.backgroundColor = color;
            style.color = '#ffffff';
            break;
    }

    return (
        <span className={baseClasses} style={style}>
            {icon && <span className="w-3.5 h-3.5">{icon}</span>}
            {data.text || ''}
        </span>
    );
}

// ── NEW WIDGETS ──────────────────────────────────────

function CardWidget({ data, s, style }) {
    // Card is a visual container — content is rendered by child widgets
    // When used standalone, shows a subtle placeholder
    return (
        <div className="w-full min-h-[60px] flex items-center justify-center">
            <span className="text-xs text-gray-400 italic">Contenedor tarjeta</span>
        </div>
    );
}

function EmbedWidget({ data }) {
    const aspectMap = { '16:9': 'aspect-video', '4:3': 'aspect-[4/3]', '1:1': 'aspect-square', 'auto': '' };
    const aspectCls = aspectMap[data.aspect] || 'aspect-video';

    if (!data.html) {
        return (
            <div className={`w-full ${aspectCls || 'aspect-video'} bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center`}>
                <svg className="w-8 h-8 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
                <span className="text-xs text-gray-400">Pega HTML/iframe aqui</span>
            </div>
        );
    }

    return (
        <div className={`w-full ${aspectCls} overflow-hidden rounded-lg`}
            dangerouslySetInnerHTML={{ __html: data.html }}
        />
    );
}

function FormWidget({ data, s, primaryColor }) {
    const fields = data.fields || [];
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);

    if (submitted) {
        return (
            <div className="text-center py-8">
                <svg className="w-12 h-12 mx-auto mb-3" style={{color: primaryColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <p className="text-base font-semibold" style={{color: s.text}}>{data.success_message || 'Gracias!'}</p>
            </div>
        );
    }

    return (
        <form onSubmit={e => { e.preventDefault(); setSubmitted(true); }} className="space-y-4">
            {fields.map((field, i) => (
                <div key={i}>
                    <label className="block text-sm font-medium mb-1.5" style={{color: s.text}}>
                        {field.label} {field.required && <span style={{color: primaryColor}}>*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                        <textarea
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            rows={3}
                            className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2"
                            style={{borderColor: '#e5e7eb', color: s.text, backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#fff', '--tw-ring-color': primaryColor}}
                            onChange={e => setFormData({...formData, [field.name]: e.target.value})}
                        />
                    ) : (
                        <input
                            type={field.type || 'text'}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            className="w-full px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2"
                            style={{borderColor: '#e5e7eb', color: s.text, backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#fff', '--tw-ring-color': primaryColor}}
                            onChange={e => setFormData({...formData, [field.name]: e.target.value})}
                        />
                    )}
                </div>
            ))}
            <button type="submit"
                className="w-full px-6 py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
                style={{backgroundColor: primaryColor}}>
                {data.submit_text || 'Enviar'}
            </button>
        </form>
    );
}

function MarqueeWidget({ data, s, primaryColor }) {
    const items = data.items || [];
    const speed = data.speed === 'slow' ? '40s' : data.speed === 'fast' ? '15s' : '25s';
    const direction = data.direction === 'right' ? 'reverse' : 'normal';

    if (items.length === 0) return null;

    const track = [...items, ...items]; // duplicate for seamless loop

    return (
        <div className="overflow-hidden relative">
            <div className="flex items-center gap-8 whitespace-nowrap"
                style={{
                    animation: `marquee-scroll ${speed} linear infinite`,
                    animationDirection: direction,
                }}>
                {track.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 shrink-0 px-4">
                        {item.image_url ? (
                            <img src={item.image_url} alt={item.text || ''} className="h-8 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" loading="lazy"/>
                        ) : (
                            <span className="text-sm font-medium opacity-60" style={{color: s.text}}>{item.text}</span>
                        )}
                    </div>
                ))}
            </div>
            <style>{`@keyframes marquee-scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
        </div>
    );
}

function AccordionWidget({ data, s, primaryColor }) {
    const items = data.items || [];
    const [openIdx, setOpenIdx] = useState(null);

    return (
        <div className="space-y-2">
            {items.map((item, i) => {
                const isOpen = openIdx === i;
                return (
                    <div key={i} className="border rounded-lg overflow-hidden" style={{borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}}>
                        <button onClick={() => setOpenIdx(isOpen ? null : i)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
                            style={{backgroundColor: isOpen ? hexToRgba(primaryColor, 0.05) : 'transparent'}}>
                            <span className="text-sm font-semibold" style={{color: s.text}}>{item.title}</span>
                            <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{color: primaryColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                            <div className="px-4 pb-3 text-sm leading-relaxed" style={{color: s.textSub}}>
                                {item.content}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function TabsWidget({ data, s, primaryColor }) {
    const items = data.items || [];
    const [activeTab, setActiveTab] = useState(0);

    if (items.length === 0) return null;

    return (
        <div>
            <div className="flex border-b" style={{borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}}>
                {items.map((item, i) => (
                    <button key={i} onClick={() => setActiveTab(i)}
                        className="px-4 py-2.5 text-sm font-medium transition-colors relative"
                        style={{color: activeTab === i ? primaryColor : s.textSub}}>
                        {item.title}
                        {activeTab === i && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{backgroundColor: primaryColor}}/>
                        )}
                    </button>
                ))}
            </div>
            <div className="pt-4 text-sm leading-relaxed" style={{color: s.text}}>
                {items[activeTab]?.content || ''}
            </div>
        </div>
    );
}

function CountdownWidget({ data, s, primaryColor }) {
    const [timeLeft, setTimeLeft] = useState({days: 0, hours: 0, minutes: 0, seconds: 0});

    useEffect(() => {
        if (!data.target_date) return;
        const target = new Date(data.target_date).getTime();
        const update = () => {
            const now = Date.now();
            const diff = Math.max(0, target - now);
            setTimeLeft({
                days: Math.floor(diff / 86400000),
                hours: Math.floor((diff % 86400000) / 3600000),
                minutes: Math.floor((diff % 3600000) / 60000),
                seconds: Math.floor((diff % 60000) / 1000),
            });
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [data.target_date]);

    if (!data.target_date) {
        return (
            <div className="text-center py-4">
                <span className="text-xs text-gray-400">Configura una fecha objetivo</span>
            </div>
        );
    }

    const units = [
        data.show_days !== false && { value: timeLeft.days, label: 'Dias' },
        data.show_hours !== false && { value: timeLeft.hours, label: 'Horas' },
        data.show_minutes !== false && { value: timeLeft.minutes, label: 'Min' },
        data.show_seconds !== false && { value: timeLeft.seconds, label: 'Seg' },
    ].filter(Boolean);

    return (
        <div>
            {data.label && <p className="text-sm font-medium mb-4" style={{color: s.textSub}}>{data.label}</p>}
            <div className="flex items-center justify-center gap-3">
                {units.map((unit, i) => (
                    <div key={i} className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold"
                            style={{backgroundColor: hexToRgba(primaryColor, 0.1), color: primaryColor}}>
                            {String(unit.value).padStart(2, '0')}
                        </div>
                        <span className="text-[10px] font-medium mt-1.5 uppercase tracking-wider" style={{color: s.textSub}}>{unit.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Main dispatcher ─────────────────────────────────

const RENDERERS = {
    heading: (props) => <HeadingWidget {...props} />,
    rich_text: (props) => <RichTextWidget {...props} />,
    image: (props) => <ImageWidget {...props} />,
    button: (props) => <ButtonWidget {...props} />,
    icon_text: (props) => <IconTextWidget {...props} />,
    list: (props) => <ListWidget {...props} />,
    video_embed: (props) => <VideoEmbedWidget {...props} />,
    spacer: (props) => <SpacerWidget {...props} />,
    divider: (props) => <DividerWidget {...props} />,
    social_links: (props) => <SocialLinksWidget {...props} />,
    stat_counter: (props) => <StatCounterWidget {...props} />,
    testimonial_card: (props) => <TestimonialCardWidget {...props} />,
    badge: (props) => <BadgeWidget {...props} />,
    card: (props) => <CardWidget {...props} />,
    embed: (props) => <EmbedWidget {...props} />,
    form: (props) => <FormWidget {...props} />,
    marquee: (props) => <MarqueeWidget {...props} />,
    accordion: (props) => <AccordionWidget {...props} />,
    tabs: (props) => <TabsWidget {...props} />,
    countdown: (props) => <CountdownWidget {...props} />,
};

export default function WidgetRenderer({ widget, s, primaryColor, secondaryColor, enableAnimations, mediaFolders, visible, widgetIndex }) {
    const data = widget.data || {};
    const style = widget.style || {};
    const renderer = RENDERERS[widget.type];
    if (!renderer) return null;

    return (
        <WidgetWrapper widget={widget} visible={visible} widgetIndex={widgetIndex}>
            <style dangerouslySetInnerHTML={{ __html: ANIM_CSS }} />
            {renderer({ data, style, s, primaryColor, secondaryColor, enableAnimations, mediaFolders, visible })}
        </WidgetWrapper>
    );
}
